// Task #3822 — hook e client API per i Progetti del PA (spazio condiviso).
// Tipi di risposta condivisi da @easyflow/shared (Zod + z.infer): nessun tipo
// locale che possa nascondere drift col backend.
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import type {
  AddPaProjectMemberRequest,
  CreatePaProjectRequest,
  PaArtifact,
  PaProject,
  PaProjectConversation,
  PaProjectConversationMessage,
  PaProjectDetail,
  PaProjectSkill,
  TenantUserSearchResult,
  UpdatePaProjectRequest,
} from '@easyflow/shared';

const BASE = '/personal-assistant/projects';

// ---------------------------------------------------------------------------
// API client (funzioni pure, riusate dagli hook e dai componenti)
// ---------------------------------------------------------------------------
export const projectsApi = {
  list: async (): Promise<PaProject[]> => {
    const res = await apiClient.get<PaProject[]>(BASE);
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
  create: async (body: CreatePaProjectRequest): Promise<PaProject | null> => {
    const res = await apiClient.post<PaProject>(BASE, body);
    return res.success && res.data ? res.data : null;
  },
  detail: async (projectId: string): Promise<PaProjectDetail | null> => {
    const res = await apiClient.get<PaProjectDetail>(`${BASE}/${projectId}`);
    return res.success && res.data ? res.data : null;
  },
  update: async (projectId: string, body: UpdatePaProjectRequest): Promise<PaProject | null> => {
    const res = await apiClient.patch<PaProject>(`${BASE}/${projectId}`, body);
    return res.success && res.data ? res.data : null;
  },
  remove: async (projectId: string): Promise<boolean> => {
    const res = await apiClient.delete<unknown>(`${BASE}/${projectId}`);
    return res.success === true;
  },
  addMember: async (projectId: string, body: AddPaProjectMemberRequest): Promise<boolean> => {
    const res = await apiClient.post<unknown>(`${BASE}/${projectId}/members`, body);
    return res.success === true;
  },
  removeMember: async (projectId: string, userId: string): Promise<boolean> => {
    const res = await apiClient.delete<unknown>(`${BASE}/${projectId}/members/${userId}`);
    return res.success === true;
  },
  setSkills: async (projectId: string, skillIds: string[]): Promise<boolean> => {
    const res = await apiClient.put<unknown>(`${BASE}/${projectId}/skills`, { skillIds });
    return res.success === true;
  },
  skillsCatalog: async (): Promise<PaProjectSkill[]> => {
    const res = await apiClient.get<PaProjectSkill[]>(`${BASE}/skills-catalog`);
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
  ensureKnowledgeAgent: async (projectId: string): Promise<string | null> => {
    const res = await apiClient.post<{ knowledgeAgentId: string }>(`${BASE}/${projectId}/knowledge-agent`, {});
    return res.success && res.data ? res.data.knowledgeAgentId : null;
  },
  uploadKnowledgeDoc: async (agentId: string, file: File): Promise<boolean> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<unknown>(`/knowledge-base/${agentId}/documents`, formData);
    return res.success === true;
  },
  deleteKnowledgeDoc: async (agentId: string, docId: string): Promise<boolean> => {
    const res = await apiClient.delete<unknown>(`/knowledge-base/${agentId}/documents/${docId}`);
    return res.success === true;
  },
  conversations: async (projectId: string): Promise<PaProjectConversation[]> => {
    const res = await apiClient.get<PaProjectConversation[]>(`${BASE}/${projectId}/conversations`);
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
  conversationMessages: async (projectId: string, convId: string): Promise<PaProjectConversationMessage[]> => {
    const res = await apiClient.get<PaProjectConversationMessage[]>(`${BASE}/${projectId}/conversations/${convId}/messages`);
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
  artifacts: async (projectId: string): Promise<PaArtifact[]> => {
    const res = await apiClient.get<PaArtifact[]>(`${BASE}/${projectId}/artifacts`);
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
  searchTenantUsers: async (query: string): Promise<TenantUserSearchResult[]> => {
    const res = await apiClient.get<TenantUserSearchResult[]>(
      `/users/search?search=${encodeURIComponent(query)}&limit=8`,
    );
    return res.success && Array.isArray(res.data) ? res.data : [];
  },
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
export function usePaProjects(active: boolean) {
  const [projects, setProjects] = useState<PaProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProjects(await projectsApi.list());
    } catch (err) {
      console.error('[usePaProjects] load failed', err);
      setError('Impossibile caricare i progetti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) void refresh();
  }, [active, refresh]);

  return { projects, loading, error, refresh };
}

export function usePaProjectDetail(projectId: string | null) {
  const [detail, setDetail] = useState<PaProjectDetail | null>(null);
  const [conversations, setConversations] = useState<PaProjectConversation[]>([]);
  const [artifacts, setArtifacts] = useState<PaArtifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setDetail(null);
      setConversations([]);
      setArtifacts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [d, convs, arts] = await Promise.all([
        projectsApi.detail(projectId),
        projectsApi.conversations(projectId),
        projectsApi.artifacts(projectId),
      ]);
      setDetail(d);
      setConversations(convs);
      setArtifacts(arts);
      if (!d) setError('Progetto non trovato');
    } catch (err) {
      console.error('[usePaProjectDetail] load failed', err);
      setError('Impossibile caricare il progetto');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  return { detail, conversations, artifacts, loading, error, refresh };
}
